import { Profile } from '../lib/supabase';

export type SupportedLicense = 'free' | 'commercial' | 'template_pack' | 'professional' | 'agency';
export type AppEntitlement = 'commercial' | 'professional' | 'template_pack' | 'agency' | 'reseller';
export type ProductSlug = 'frontend' | 'professional' | 'template_pack' | 'agency' | 'reseller' | 'bundle' | 'fastpass';

const LEGACY_LICENSE_ENTITLEMENTS: Record<SupportedLicense, AppEntitlement[]> = {
  free: [],
  commercial: ['commercial'],
  template_pack: ['commercial', 'template_pack'],
  professional: ['commercial', 'professional'],
  agency: ['commercial', 'professional', 'agency'],
};

const PRODUCT_CREDIT_GRANTS: Record<ProductSlug, number> = {
  frontend: 275,
  professional: 725,
  template_pack: 500,
  agency: 1100,
  reseller: 0,
  bundle: 2600,
  fastpass: 2325,
};

const PRODUCT_ENTITLEMENTS: Record<ProductSlug, AppEntitlement[]> = {
  frontend: ['commercial'],
  professional: ['professional'],
  template_pack: ['template_pack'],
  agency: ['agency'],
  reseller: ['reseller'],
  bundle: ['commercial', 'professional', 'template_pack', 'agency', 'reseller'],
  fastpass: ['professional', 'template_pack', 'agency', 'reseller'],
};

function normalizeStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.filter((item): item is string => typeof item === 'string');
      }
    } catch {
      return [];
    }
  }

  return [];
}

function unique<T extends string>(values: T[]): T[] {
  return Array.from(new Set(values));
}

function getEntitlementsForProducts(productSlugs: ProductSlug[]): AppEntitlement[] {
  return unique(
    productSlugs.flatMap((productSlug) => PRODUCT_ENTITLEMENTS[productSlug] || []),
  );
}

export function getProfileProducts(profile: Partial<Profile> | null | undefined): ProductSlug[] {
  const productSlugs = normalizeStringArray(profile?.jvzoo_products).filter(
    (value): value is ProductSlug =>
      ['frontend', 'professional', 'template_pack', 'agency', 'reseller', 'bundle', 'fastpass'].includes(value),
  );

  return unique(productSlugs);
}

export function getProfileEntitlements(profile: Partial<Profile> | null | undefined): AppEntitlement[] {
  const productEntitlements = getEntitlementsForProducts(getProfileProducts(profile));
  const storedEntitlements = normalizeStringArray(profile?.license_entitlements).filter(
    (value): value is AppEntitlement =>
      ['commercial', 'professional', 'template_pack', 'agency', 'reseller'].includes(value),
  );

  const legacyLicense = (profile?.license || 'free') as SupportedLicense;
  const hasExplicitState = productEntitlements.length > 0 || storedEntitlements.length > 0;
  const legacyEntitlements = hasExplicitState ? [] : (LEGACY_LICENSE_ENTITLEMENTS[legacyLicense] || []);

  return unique([...legacyEntitlements, ...storedEntitlements, ...productEntitlements]);
}

export function hasEntitlement(profile: Partial<Profile> | null | undefined, entitlement: AppEntitlement): boolean {
  return getProfileEntitlements(profile).includes(entitlement);
}

export function getPrimaryLicense(profile: Partial<Profile> | null | undefined): SupportedLicense {
  const entitlements = getProfileEntitlements(profile);

  if (entitlements.includes('agency')) return 'agency';
  if (entitlements.includes('professional')) return 'professional';
  if (entitlements.includes('template_pack')) return 'template_pack';
  if (entitlements.includes('commercial')) return 'commercial';
  return 'free';
}

export function hasProfessionalAccess(profile: Partial<Profile> | null | undefined): boolean {
  const entitlements = getProfileEntitlements(profile);
  return entitlements.includes('professional') || entitlements.includes('agency');
}

export function hasAgencyAccess(profile: Partial<Profile> | null | undefined): boolean {
  return getProfileEntitlements(profile).includes('agency');
}

export function getLicenseSummary(profile: Partial<Profile> | null | undefined): string {
  const products = getProfileProducts(profile);
  const entitlements = getProfileEntitlements(profile);

  if (products.includes('bundle')) return 'Bundle';
  if (products.includes('fastpass')) return entitlements.includes('commercial') ? 'Commercial + Fastpass' : 'Fastpass';

  let main = 'Free';
  if (entitlements.includes('agency')) {
    main = 'Agency';
  } else if (entitlements.includes('professional')) {
    main = 'Professional';
  } else if (entitlements.includes('commercial')) {
    main = 'Commercial';
  }

  const addOns: string[] = [];
  if (entitlements.includes('template_pack')) {
    addOns.push('Template Pack');
  }
  if (entitlements.includes('reseller')) {
    addOns.push('Reseller');
  }

  if (main === 'Free' && addOns.length === 0) {
    return main;
  }

  return [main, ...addOns].join(' + ');
}

export function getAvailableUpgradeIds(profile: Partial<Profile> | null | undefined): Array<'commercial' | 'template_pack' | 'professional' | 'agency'> {
  const entitlements = getProfileEntitlements(profile);
  const upgrades: Array<'commercial' | 'template_pack' | 'professional' | 'agency'> = [];

  if (!entitlements.includes('commercial')) upgrades.push('commercial');
  if (!entitlements.includes('template_pack')) upgrades.push('template_pack');
  if (!entitlements.includes('professional')) upgrades.push('professional');
  if (!entitlements.includes('agency')) upgrades.push('agency');

  return upgrades;
}

export function getOwnedLicenseCount(profile: Partial<Profile> | null | undefined): number {
  const entitlements = getProfileEntitlements(profile);

  return ['commercial', 'professional', 'template_pack', 'agency', 'reseller'].filter(
    (entitlement) => entitlements.includes(entitlement as AppEntitlement),
  ).length;
}

export function getReferenceCreditCapacity(profile: Partial<Profile> | null | undefined): number {
  const products = getProfileProducts(profile);

  if (products.length > 0) {
    return products.reduce((sum, productSlug) => sum + PRODUCT_CREDIT_GRANTS[productSlug], 0) || 10;
  }

  switch (getPrimaryLicense(profile)) {
    case 'agency':
      return 1100;
    case 'professional':
      return 725;
    case 'template_pack':
      return 500;
    case 'commercial':
      return 275;
    default:
      return 10;
  }
}
